// src/pages/DashboardPage.tsx

import React from "react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import {
  Users, Megaphone, DollarSign, TrendingUp,
  AlertTriangle, CheckCircle, Info, ArrowRight,
  Plus, BarChart2, Shield, Brain, Tv, Smartphone,
  Zap, Globe,
} from "lucide-react";
import { clsx } from "clsx";
import KpiCard from "@/components/ui/KpiCard";
import { CAMPAIGNS, intentTopics } from "@/data"
import { dashboardKPIs, identityGraphStats } from "@/data/identityGraph";
import { useAuth } from "@/context/AuthContext";

// ── Spend trend (last 12 weeks) ───────────────────────────────
const SPEND_TREND = [
  { week: "W1",  spend: 280, impressions: 42 },
  { week: "W2",  spend: 310, impressions: 48 },
  { week: "W3",  spend: 298, impressions: 45 },
  { week: "W4",  spend: 342, impressions: 52 },
  { week: "W5",  spend: 380, impressions: 58 },
  { week: "W6",  spend: 365, impressions: 55 },
  { week: "W7",  spend: 410, impressions: 64 },
  { week: "W8",  spend: 428, impressions: 67 },
  { week: "W9",  spend: 398, impressions: 61 },
  { week: "W10", spend: 445, impressions: 70 },
  { week: "W11", spend: 468, impressions: 74 },
  { week: "W12", spend: 428, impressions: 68 },
];

const CHANNEL_MIX = [
  { channel: "Display",  spend: 1_840, roas: 5.2 },
  { channel: "O&O",      spend: 1_240, roas: 7.1 },
  { channel: "CTV",      spend: 980,   roas: 6.4 },
  { channel: "Video",    spend: 620,   roas: 5.8 },
];

const ACTIVITY = [
  { icon: Megaphone,    color: "bg-blue/10 text-blue",          title: "Campaign launched",       body: "DeWalt Q2 Power Tools — Display is now live",     time: "2m ago"  },
  { icon: TrendingUp,   color: "bg-cyan/10 text-cyan",          title: "Intent surge detected",   body: "Outdoor & Garden +340% week-over-week",           time: "18m ago" },
  { icon: Users,        color: "bg-navy/10 text-navy",          title: "Audience created",        body: "Grocery Delivery Switchers — 780K reach",         time: "1h ago"  },
  { icon: BarChart2,    color: "bg-emerald-50 text-emerald-600",title: "Report ready",            body: "Samsung Galaxy S26 Attribution — March",          time: "2h ago"  },
  { icon: AlertTriangle,color: "bg-amber-50 text-amber-600",    title: "Lapse risk alert",        body: "184K Midwest Hardware members at risk",           time: "3h ago"  },
  { icon: CheckCircle,  color: "bg-emerald-50 text-emerald-600",title: "Partner onboarded",       body: "StyleNow Apparel data sync complete — 87% match", time: "5h ago"  },
];

const ALERTS = [
  { severity: "error",   icon: AlertTriangle, title: "Budget pacing warning",        message: "P&G Tide is 94% spent with 18 days remaining.",           cta: "Review campaign"   },
  { severity: "warning", icon: AlertTriangle, title: "Lapse risk — 184K members",    message: "Midwest Hardware members showing competitor intent surge.", cta: "View lapse report" },
  { severity: "info",    icon: TrendingUp,    title: "Intent surge: Outdoor & Garden",message: "+340% WoW. Spring season demand accelerating.",           cta: "Build audience"    },
  { severity: "info",    icon: Info,          title: "Data refresh complete",         message: "FreshMart loyalty file refreshed. 4.78M records matched.", cta: "View dashboard"   },
];

const SEVERITY_STYLES = {
  error:   "border-red-200 bg-red-50",
  warning: "border-amber-200 bg-amber-50",
  info:    "border-blue/20 bg-blue/5",
  success: "border-emerald-200 bg-emerald-50",
};
const SEVERITY_ICON_STYLES = {
  error:   "text-red-500",
  warning: "text-amber-500",
  info:    "text-blue",
  success: "text-emerald-500",
};

const STATUS_STYLES: Record<string, string> = {
  active: "badge-green", paused: "badge-yellow", draft: "badge-slate", completed: "badge-navy",
};
const CHANNEL_LABELS: Record<string, string> = {
  oo: "O&O", display: "Display", ctv: "CTV", video: "Video", social: "Social",
};

// ── Compact number formatter ──────────────────────────────────
function fmt(n: number): string {
  if (n >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(1)}T`;
  if (n >= 1_000_000_000)     return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)         return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)             return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

export default function DashboardPage() {
  const { user } = useAuth();
  const activeCampaigns = CAMPAIGNS.filter(c => c.status === "active").slice(0, 5);
  const surgingTopics   = [...intentTopics].sort((a, b) => ((b.count7d - b.count30d/4) / (b.count30d/4) * 100) - ((a.count7d - a.count30d/4) / (a.count30d/4) * 100)).slice(0, 4);

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Page header */}
      <div className="page-header mb-0">
        <h1 className="page-title">Good morning, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="page-subtitle">Here's what's happening across the MRN platform today.</p>
      </div>

      {/* Row 1 — Campaign KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Reach" value={dashboardKPIs.totalReach} format="compact" delta={8.2}
          icon={<Users size={16} className="text-blue" />} iconBg="bg-blue/10" sublabel="Verified individuals" />
        <KpiCard label="Active Campaigns" value={dashboardKPIs.activeCampaigns} format="number" delta={12.5}
          icon={<Megaphone size={16} className="text-cyan" />} iconBg="bg-cyan/10" sublabel="Across all advertisers" />
        <KpiCard label="Spend MTD" value={dashboardKPIs.spendToDate} format="currency" delta={14.3}
          icon={<DollarSign size={16} className="text-emerald-600" />} iconBg="bg-emerald-50" sublabel="March 2026" />
        <KpiCard label="Avg ROAS" value={dashboardKPIs.overallROAS} format="multiplier" delta={6.8}
          icon={<TrendingUp size={16} className="text-navy" />} iconBg="bg-navy/10" sublabel="Data-driven attribution" />
      </div>

      {/* Row 2 — Platform KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Suppression Savings" value={dashboardKPIs.suppressionSavings} format="currency" delta={18.4}
          icon={<Shield size={16} className="text-blue" />} iconBg="bg-blue/10" sublabel="Waste eliminated MTD" />
        <KpiCard label="Match Rate" value={dashboardKPIs.matchRate * 100} format="percent" delta={1.2}
          icon={<CheckCircle size={16} className="text-cyan" />} iconBg="bg-cyan/10" sublabel="Avg across all partners" />
        <KpiCard label="Weekly Intent Signals" value={dashboardKPIs.weeklyIntentSignals} format="compact" delta={4.1}
          icon={<Brain size={16} className="text-navy" />} iconBg="bg-navy/10" sublabel="B2C + B2B combined" />
        <KpiCard label="Intent Topics" value={identityGraphStats.intentSignals.b2cTopics} format="compact"
          icon={<Zap size={16} className="text-emerald-600" />} iconBg="bg-emerald-50" sublabel="8,305 active topics" />
      </div>

      {/* Row 3 — Identity Graph scale */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-navy">Super Identity Graph</h3>
            <p className="text-xs text-slate mt-0.5">Real-time scale across all identity assets</p>
          </div>
          <span className="badge badge-green">Live · Refreshed weekly</span>
        </div>
        <div className="grid grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: "Individuals",     value: identityGraphStats.individuals,              icon: Users,       color: "bg-navy/10 text-navy"          },
            { label: "Email IDs",       value: identityGraphStats.email.totalEmailIds,             icon: Zap,         color: "bg-blue/10 text-blue"          },
            { label: "Phone Records",   value: identityGraphStats.phone.total,             icon: Zap,         color: "bg-cyan/10 text-cyan"          },
            { label: "Mobile Devices",  value: identityGraphStats.mobileDevices.total,      icon: Smartphone,  color: "bg-blue/10 text-blue"          },
            { label: "CTV IFAs",        value: identityGraphStats.ctv.totalIFA,      icon: Tv,          color: "bg-navy/10 text-navy"          },
            { label: "IP Records",      value: identityGraphStats.ipAddresses.total,        icon: Globe,       color: "bg-cyan/10 text-cyan"          },
            { label: "Behavioral Recs", value: identityGraphStats.behavioral.iabRecords,    icon: Brain,       color: "bg-emerald-50 text-emerald-600"},
          ].map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
              <div className={clsx("w-7 h-7 rounded-lg flex items-center justify-center mx-auto mb-2", item.color)}>
                <item.icon size={13} />
              </div>
              <p className="text-sm font-bold text-navy tabular leading-tight">{fmt(item.value)}</p>
              <p className="text-2xs text-slate mt-0.5 leading-tight">{item.label}</p>
            </div>
          ))}
        </div>

        {/* CTV + Mobile callout */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div className="bg-navy/5 border border-navy/10 rounded-lg p-3 flex items-center gap-3">
            <Tv size={18} className="text-navy flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-navy">{fmt(identityGraphStats.ctv.linkedToIdentity)} CTV devices linked</p>
              <p className="text-2xs text-slate">{(identityGraphStats.ctv.matchRate * 100).toFixed(1)}% of total IFAs matched to person</p>
            </div>
          </div>
          <div className="bg-blue/5 border border-blue/10 rounded-lg p-3 flex items-center gap-3">
            <Smartphone size={18} className="text-blue flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-blue">{fmt(identityGraphStats.mobileDevices.total)} mobile devices</p>
              <p className="text-2xs text-slate">{fmt(identityGraphStats.mobileDevices.android)} Android · {fmt(identityGraphStats.mobileDevices.apple)} iOS</p>
            </div>
          </div>
          <div className="bg-cyan/5 border border-cyan/10 rounded-lg p-3 flex items-center gap-3">
            <Brain size={18} className="text-cyan flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-cyan">{fmt(dashboardKPIs.weeklyIntentSignals)} weekly signals</p>
              <p className="text-2xs text-slate">{identityGraphStats.intentSignals.b2cTopics.toLocaleString()} topics · 1-day latency</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-navy">Weekly Spend & Impressions</h3>
              <p className="text-xs text-slate mt-0.5">Last 12 weeks — all CAMPAIGNS</p>
            </div>
            <span className="badge badge-green">+14.3% MoM</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={SPEND_TREND} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3056D3" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#3056D3" stopOpacity={0}    />
                </linearGradient>
                <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#0891B2" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#0891B2" stopOpacity={0}    />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                formatter={(v: number, name: string) => [name === "spend" ? `$${v}K` : `${v}M`, name === "spend" ? "Spend" : "Impressions"]} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="spend"       stroke="#3056D3" strokeWidth={2} fill="url(#spendGrad)" name="spend"       />
              <Area type="monotone" dataKey="impressions" stroke="#0891B2" strokeWidth={2} fill="url(#impGrad)"   name="impressions" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-navy">Channel Mix</h3>
            <p className="text-xs text-slate mt-0.5">Spend ($K) vs ROAS</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={CHANNEL_MIX} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="channel" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                formatter={(v: number, name: string) => [name === "spend" ? `$${v}K` : `${v}x`, name === "spend" ? "Spend" : "ROAS"]} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="spend" fill="#3056D3" radius={[4,4,0,0]} name="spend" />
              <Bar dataKey="roas"  fill="#0891B2" radius={[4,4,0,0]} name="roas"  />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Main content row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Active CAMPAIGNS */}
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-navy">Active Campaigns</h3>
            <button className="btn-ghost btn-sm flex items-center gap-1">View all <ArrowRight size={12} /></button>
          </div>
          <table className="table-base">
            <thead><tr><th>Campaign</th><th>Channels</th><th>Spent</th><th>ROAS</th><th>Status</th></tr></thead>
            <tbody>
              {activeCampaigns.map(c => (
                <tr key={c.id} className="cursor-pointer">
                  <td>
                    <div className="font-medium text-gray-800 truncate-cell">{c.name}</div>
                    <div className="text-xs text-slate">{c.advertiser}</div>
                  </td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {c.channels.slice(0, 2).map(ch => <span key={ch} className="badge badge-slate">{CHANNEL_LABELS[ch]}</span>)}
                      {c.channels.length > 2 && <span className="badge badge-slate">+{c.channels.length - 2}</span>}
                    </div>
                  </td>
                  <td className="tabular font-medium">${(c.spent / 1000).toFixed(0)}K</td>
                  <td>
                    <span className={clsx("font-semibold tabular", c.roas >= 5 ? "text-emerald-600" : "text-amber-600")}>
                      {c.roas.toFixed(1)}x
                    </span>
                  </td>
                  <td><span className={clsx("badge", STATUS_STYLES[c.status])}>{c.status.charAt(0).toUpperCase() + c.status.slice(1)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Alerts */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-navy">Alerts</h3>
            <span className="badge badge-red">{ALERTS.length}</span>
          </div>
          <div className="space-y-2">
            {ALERTS.map((alert, i) => (
              <div key={i} className={clsx("rounded-lg border p-3", SEVERITY_STYLES[alert.severity as keyof typeof SEVERITY_STYLES])}>
                <div className="flex items-start gap-2">
                  <alert.icon size={14} className={clsx("mt-0.5 flex-shrink-0", SEVERITY_ICON_STYLES[alert.severity as keyof typeof SEVERITY_ICON_STYLES])} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800">{alert.title}</p>
                    <p className="text-xs text-slate mt-0.5 leading-relaxed">{alert.message}</p>
                    {alert.cta && (
                      <button className="text-xs font-medium text-blue hover:text-navy mt-1 flex items-center gap-0.5 transition-colors">
                        {alert.cta} <ArrowRight size={10} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Activity feed */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-navy">Activity Feed</h3>
            <button className="btn-ghost btn-sm">View all</button>
          </div>
          <div className="space-y-3">
            {ACTIVITY.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={clsx("w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0", item.color)}>
                  <item.icon size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800">{item.title}</p>
                  <p className="text-xs text-slate truncate">{item.body}</p>
                </div>
                <span className="text-2xs text-slate flex-shrink-0">{item.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Intent surges + quick actions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-navy">Intent Surges This Week</h3>
            <button className="btn-ghost btn-sm flex items-center gap-1">Explore <ArrowRight size={12} /></button>
          </div>
          <div className="space-y-3">
            {surgingTopics.map(topic => (
              <div key={topic.id} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-800">{topic.topic}</span>
                    <span className="text-xs font-semibold text-emerald-600">+{((topic.count7d - topic.count30d/4) / (topic.count30d/4) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-gradient-to-r from-blue to-cyan h-1.5 rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, Math.round(topic.count7d / 200000))}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-2xs text-slate">{(topic.count7d / 1_000_000).toFixed(1)}M in-market</span>
                    <span className="text-2xs text-slate">Intensity: {Math.min(100, Math.round(topic.count7d / 200000))}/100</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="section-label">Quick Actions</p>
            <div className="grid grid-cols-2 gap-2">
              <button className="btn-secondary btn-sm justify-start gap-2"><Plus size={12} /> New Audience</button>
              <button className="btn-secondary btn-sm justify-start gap-2"><Megaphone size={12} /> New Campaign</button>
              <button className="btn-secondary btn-sm justify-start gap-2"><BarChart2 size={12} /> Run Report</button>
              <button className="btn-secondary btn-sm justify-start gap-2"><Brain size={12} /> Intent Explorer</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}